/**
 * Studio AI Store
 * State management for video and image generation using Zustand
 */

import { create } from "zustand"

import type { ChatSlice } from "./slices/chat-slice"
import type {
  Image,
  ImageGenerationRequest,
  ImageGenerationResult,
  MediaJob,
  Project,
  Sora2Settings,
  StudioAIView,
  StyleAdherence,
  Video,
  VideoTemplate,
} from "@/features/studio-ai/types/studio-ai.types"

import { generateImage, listUserJobs } from "@/api/studio-ai-api"
import { updateSettingsSection } from "@/api/user-settings-api"
import {
  ImageQuality,
  ImageSize,
  MediaType,
  VideoStyle,
} from "@/features/studio-ai/types/studio-ai.types"
import { ImageCacheService } from "@/services/image-cache-service"

import { createChatSlice } from "./slices/chat-slice"

// Default video settings
const defaultSettings: Sora2Settings = {
  duration: 4,
  size: "1280x720",
  use_high_quality: true,
  quality: "standard",
  style: VideoStyle.Custom,
}

// Default image settings
export interface ImageSettings {
  size: ImageSize
  quality: ImageQuality
  style: VideoStyle
  n: number
  compression: number
  outputFormat: "png" | "jpeg"
  background: "opaque" | "transparent"
  /**
   * Registry id of the chosen image model. Undefined means "use the Studio
   * default from GET /studio-ai/settings". A persisted id can outlive the
   * deployment it names, so consumers resolve it against the current model
   * list (see `useSelectedImageModel`) rather than trusting it here.
   */
  modelId?: string
}

const defaultImageSettings: ImageSettings = {
  size: ImageSize.Square1024,
  quality: ImageQuality.Medium,
  style: VideoStyle.Realistic,
  n: 1,
  compression: 100,
  outputFormat: "png",
  background: "opaque",
}

// Generation state
interface GenerationState {
  isGenerating: boolean
  currentJobId: string | null
  progress: number
  status: string
  estimatedTime?: number
  /** Latest partial image data URL while streaming. Null until first partial arrives. */
  partialImageUrl?: string | null
}

// Feature capabilities from server
interface StudioCapabilities {
  httpEndpointEnabled: boolean
}

type StudioAIStore = ChatSlice & {
  // View state (landing, wizard, style-template-creator)
  currentView: StudioAIView
  setCurrentView: (view: StudioAIView) => void

  // Wizard state
  currentWizardStep: number
  setCurrentWizardStep: (step: number) => void
  wizardPrompt: string
  setWizardPrompt: (prompt: string) => void
  resetWizard: () => void

  // Style template selection
  selectedStyleTemplateId: string | null
  setSelectedStyleTemplateId: (id: string | null) => void
  selectedStyleAdherence: StyleAdherence
  setSelectedStyleAdherence: (adherence: StyleAdherence) => void

  // Media type selection
  mediaType: MediaType
  setMediaType: (type: MediaType) => void

  // Feature capabilities
  capabilities: StudioCapabilities
  setCapabilities: (capabilities: Partial<StudioCapabilities>) => void

  // Video settings
  settings: Sora2Settings
  updateSettings: (settings: Partial<Sora2Settings>) => void

  // Image settings
  imageSettings: ImageSettings
  updateImageSettings: (settings: Partial<ImageSettings>) => void

  // Video-specific per-request parameters
  remixVideoId: string | null
  setRemixVideoId: (id: string | null) => void
  videoReferenceImageUrl: string | null
  setVideoReferenceImageUrl: (url: string | null) => void

  // Image-specific per-request parameters
  imageReferenceUrl: string | null
  setImageReferenceUrl: (url: string | null) => void

  // Image editor state
  editorImageUrl: string | null
  setEditorImageUrl: (url: string | null) => void
  // job_id of the image currently shown in the editor. When set, the editor
  // resolves the display/fetch URL through the batch-prefetch cache so the
  // <img> tag and fetch() calls get a blob: URL that doesn't need auth.
  // null for ad-hoc URLs (data: from a fresh generation, or a SAS link).
  editorImageJobId: string | null
  setEditorImageJobId: (id: string | null) => void
  editorSourceJobId: string | null
  setEditorSourceJobId: (id: string | null) => void

  // Current state
  currentProjectId: string | null
  setCurrentProjectId: (id: string | null) => void

  // Generation state
  generationState: GenerationState
  updateGenerationState: (state: Partial<GenerationState>) => void
  resetGenerationState: () => void

  // Media jobs (unified videos and images)
  jobs: MediaJob[]
  addJob: (job: MediaJob) => void
  updateJob: (jobId: string, updates: Partial<MediaJob>) => void
  removeJob: (jobId: string) => void
  setJobs: (jobs: MediaJob[]) => void
  fetchUserJobs: (status?: string, mediaType?: string) => Promise<void>

  // Legacy video accessors (for backward compatibility)
  videos: Video[]
  addVideo: (video: Video) => void
  updateVideo: (jobId: string, updates: Partial<Video>) => void
  removeVideo: (jobId: string) => void
  setVideos: (videos: Video[]) => void

  // Images
  images: Image[]
  addImage: (image: Image) => void
  updateImage: (jobId: string, updates: Partial<Image>) => void
  removeImage: (jobId: string) => void

  // Projects
  projects: Project[]
  setProjects: (projects: Project[]) => void
  addProject: (project: Project) => void
  updateProject: (projectId: string, updates: Partial<Project>) => void

  // Templates
  templates: VideoTemplate[]
  setTemplates: (templates: VideoTemplate[]) => void

  // Image generation
  generateImage: (request: ImageGenerationRequest) => Promise<ImageGenerationResult>
}

const initialGenerationState: GenerationState = {
  isGenerating: false,
  currentJobId: null,
  progress: 0,
  status: "",
  partialImageUrl: null,
}

export const useStudioAIStore = create<StudioAIStore>()((set, get, api) => ({
  ...createChatSlice(set, get, api),

  // View state
  currentView: "landing" as StudioAIView,
  setCurrentView: (view: StudioAIView) => {
    set({ currentView: view })
  },

  // Wizard state
  currentWizardStep: 0,
  setCurrentWizardStep: (step: number) => {
    set({ currentWizardStep: step })
  },
  wizardPrompt: "",
  setWizardPrompt: (prompt: string) => {
    set({ wizardPrompt: prompt })
  },
  resetWizard: () => {
    set({
      currentView: "landing",
      currentWizardStep: 0,
      wizardPrompt: "",
      settings: defaultSettings,
      imageSettings: defaultImageSettings,
      remixVideoId: null,
      videoReferenceImageUrl: null,
      imageReferenceUrl: null,
      selectedStyleTemplateId: null,
      selectedStyleAdherence: "inspired" as StyleAdherence,
    })
  },

  // Style template selection
  selectedStyleTemplateId: null,
  setSelectedStyleTemplateId: (id: string | null) => {
    set({ selectedStyleTemplateId: id })
  },
  selectedStyleAdherence: "inspired" as StyleAdherence,
  setSelectedStyleAdherence: (adherence: StyleAdherence) => {
    set({ selectedStyleAdherence: adherence })
  },

  // Media type selection
  mediaType: MediaType.Video,
  setMediaType: (type: MediaType) => {
    set({ mediaType: type })
  },

  // Feature capabilities
  capabilities: {
    httpEndpointEnabled: false, // Default to false until loaded from server
  },
  setCapabilities: (newCapabilities: Partial<StudioCapabilities>) => {
    set({ capabilities: { ...get().capabilities, ...newCapabilities } })
  },

  // Video settings
  settings: defaultSettings,
  updateSettings: (newSettings: Partial<Sora2Settings>) => {
    const updated = { ...get().settings, ...newSettings }
    set({ settings: updated })
    // Save to database in background (silently fail if error)
    void updateSettingsSection("studio_ai", updated)
  },

  // Image settings
  imageSettings: defaultImageSettings,
  updateImageSettings: (newSettings: Partial<ImageSettings>) => {
    // Multi-image generation is currently disabled while the backend
    // streams a single image per call. Force n=1 until the fan-out path
    // ships so a stale persisted value (or future stray writes) can't
    // trigger the server-side rejection.
    const updated = { ...get().imageSettings, ...newSettings, n: 1 }
    set({ imageSettings: updated })
    // Save to database in background
    void updateSettingsSection("studio_ai_images", updated)
  },

  // Video-specific per-request parameters
  remixVideoId: null,
  setRemixVideoId: (id: string | null) => {
    set({ remixVideoId: id })
  },
  videoReferenceImageUrl: null,
  setVideoReferenceImageUrl: (url: string | null) => {
    set({ videoReferenceImageUrl: url })
  },

  // Image-specific per-request parameters
  imageReferenceUrl: null,
  setImageReferenceUrl: (url: string | null) => {
    set({ imageReferenceUrl: url })
  },

  // Image editor state
  editorImageUrl: null,
  setEditorImageUrl: (url: string | null) => {
    set({ editorImageUrl: url })
  },
  editorImageJobId: null,
  setEditorImageJobId: (id: string | null) => {
    set({ editorImageJobId: id })
  },
  editorSourceJobId: null,
  setEditorSourceJobId: (id: string | null) => {
    set({ editorSourceJobId: id })
  },

  // Current state
  currentProjectId: null,
  setCurrentProjectId: (id: string | null) => {
    set({ currentProjectId: id })
  },

  // Generation state
  generationState: initialGenerationState,
  updateGenerationState: (state: Partial<GenerationState>) => {
    set({ generationState: { ...get().generationState, ...state } })
  },
  resetGenerationState: () => {
    set({ generationState: initialGenerationState })
  },

  // Unified media jobs
  jobs: [],
  addJob: (job: MediaJob) => {
    const newJobs = [job, ...get().jobs]
    const videos = newJobs.filter(
      (j) => !j.media_type || j.media_type === MediaType.Video
    ) as Video[]
    const images = newJobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs: newJobs, videos, images })
  },
  updateJob: (jobId: string, updates: Partial<MediaJob>) => {
    // Find the existing job to check if it's an image job
    const existingJob = get().jobs.find((j) => j.job_id === jobId)

    // Cache image_url if updating an image job with a new image_url
    if (existingJob?.media_type === MediaType.Image && "image_url" in updates) {
      const imageUpdates = updates as Partial<Image>
      if (imageUpdates.image_url) {
        const cached = ImageCacheService.setImage(jobId, imageUpdates.image_url)
        if (!cached) {
          // eslint-disable-next-line no-console
          console.warn(`Failed to cache updated image ${jobId} - localStorage limit reached`)
        }
      }
    }

    const newJobs = get().jobs.map((j) =>
      j.job_id === jobId ? ({ ...j, ...updates } as MediaJob) : j
    )
    const videos = newJobs.filter(
      (j) => !j.media_type || j.media_type === MediaType.Video
    ) as Video[]
    const images = newJobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs: newJobs, videos, images })
  },
  removeJob: (jobId: string) => {
    // Remove from localStorage cache when deleting
    ImageCacheService.removeImage(jobId)

    const newJobs = get().jobs.filter((j) => j.job_id !== jobId)
    const videos = newJobs.filter(
      (j) => !j.media_type || j.media_type === MediaType.Video
    ) as Video[]
    const images = newJobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs: newJobs, videos, images })
  },
  setJobs: (jobs: MediaJob[]) => {
    const videos = jobs.filter((j) => !j.media_type || j.media_type === MediaType.Video) as Video[]
    const images = jobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs, videos, images })
  },
  fetchUserJobs: async (status?: string, mediaType?: string) => {
    try {
      // Cleanup expired cache entries first
      ImageCacheService.cleanupExpired()

      const jobs = await listUserJobs(status, mediaType)

      // Restore image_url from cache for image jobs
      const jobsWithCache = jobs.map((job) => {
        if (job.media_type === MediaType.Image) {
          const imageJob = job as Image
          if (!imageJob.image_url) {
            const cachedImage = ImageCacheService.getImage(job.job_id)
            if (cachedImage) {
              return { ...imageJob, image_url: cachedImage } as MediaJob
            }
          }
        }
        return job
      })

      const videos = jobsWithCache.filter(
        (j) => !j.media_type || j.media_type === MediaType.Video
      ) as Video[]
      const images = jobsWithCache.filter((j) => j.media_type === MediaType.Image) as Image[]
      set({ jobs: jobsWithCache, videos, images })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch user jobs:", error)
      throw error
    }
  },

  // Legacy video accessors (for backward compatibility)
  videos: [],
  images: [],
  addVideo: (video: Video) => {
    const videoJob = { ...video, media_type: MediaType.Video } as MediaJob
    const newJobs = [videoJob, ...get().jobs]
    const videos = newJobs.filter(
      (j) => !j.media_type || j.media_type === MediaType.Video
    ) as Video[]
    const images = newJobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs: newJobs, videos, images })
  },
  updateVideo: (jobId: string, updates: Partial<Video>) => {
    get().updateJob(jobId, updates)
  },
  removeVideo: (jobId: string) => {
    get().removeJob(jobId)
  },
  setVideos: (videos: Video[]) => {
    const images = get().jobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    const videosWithType: MediaJob[] = videos.map(
      (v) => ({ ...v, media_type: MediaType.Video }) as Video
    )
    set({ jobs: [...videosWithType, ...images], videos, images })
  },

  // Images
  addImage: (image: Image) => {
    // Cache the image in localStorage if it has an image_url
    if (image.image_url) {
      const cached = ImageCacheService.setImage(image.job_id, image.image_url)
      if (!cached) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to cache image ${image.job_id} - localStorage limit reached`)
      }
    }

    const imageJob = { ...image, media_type: MediaType.Image } as MediaJob
    const newJobs = [imageJob, ...get().jobs]
    const videos = newJobs.filter(
      (j) => !j.media_type || j.media_type === MediaType.Video
    ) as Video[]
    const images = newJobs.filter((j) => j.media_type === MediaType.Image) as Image[]
    set({ jobs: newJobs, videos, images })
  },
  updateImage: (jobId: string, updates: Partial<Image>) => {
    get().updateJob(jobId, updates)
  },
  removeImage: (jobId: string) => {
    get().removeJob(jobId)
  },

  // Projects
  projects: [],
  setProjects: (projects: Project[]) => {
    set({ projects })
  },
  addProject: (project: Project) => {
    set({ projects: [project, ...get().projects] })
  },
  updateProject: (projectId: string, updates: Partial<Project>) => {
    set({
      projects: get().projects.map((p) => (p.id === projectId ? { ...p, ...updates } : p)),
    })
  },

  // Templates
  templates: [],
  setTemplates: (templates: VideoTemplate[]) => {
    set({ templates })
  },

  // Image generation
  generateImage: async (request: ImageGenerationRequest) => {
    try {
      set({
        generationState: {
          isGenerating: true,
          currentJobId: null,
          progress: 0,
          status: "generating",
        },
      })

      const result = await generateImage(request)

      // Add to jobs list (caching is handled automatically by addImage)
      const imageJob: Image = {
        id: result.job_id,
        job_id: result.job_id,
        project_id: request.project_id,
        prompt: result.prompt,
        original_prompt: result.original_prompt,
        revised_prompt: result.revised_prompt,
        status: result.status,
        size: result.size,
        quality: result.quality as ImageQuality,
        style: request.style as VideoStyle | undefined,
        image_url: result.image_url,
        error_message: result.error_message,
        created_at: result.created_at,
        completed_at: result.completed_at,
        metadata: result.metadata,
        media_type: MediaType.Image,
      }

      get().addImage(imageJob)

      set({
        generationState: {
          isGenerating: false,
          currentJobId: result.job_id,
          progress: 100,
          status: "completed",
        },
      })

      return result
    } catch (error) {
      set({
        generationState: {
          isGenerating: false,
          currentJobId: null,
          progress: 0,
          status: "error",
        },
      })
      throw error
    }
  },
}))
